/**
 * ScoreboardContainer Component
 *
 * Full-page container for the scoreboard with themed background.
 * Provides the visual foundation and layout structure.
 *
 * When migrating to Chakra UI:
 * - Replace styled.div with Box
 * - Use Chakra's useColorModeValue for theme-aware defaults
 * - CSS variables will map to Chakra semantic tokens
 */

import * as React from 'react';
import styled from 'styled-components';
import { useScoreboardThemeContext } from '../context/ScoreboardThemeContext';
import { SCOREBOARD_CSS_VARS } from '../hooks/useScoreboardTheme';

// =============================================================================
// Types
// =============================================================================

export interface ScoreboardContainerProps {
  children: React.ReactNode;
  /** Optional className for additional styling */
  className?: string;
  /** Whether to show the settings button */
  showSettingsButton?: boolean;
  /** Test ID for e2e testing */
  'data-testid'?: string;
}

// =============================================================================
// Styled Components (will be replaced with Chakra Box)
// =============================================================================

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  min-height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  /* Theme CSS variables are applied inline */
  color: var(${SCOREBOARD_CSS_VARS.textPrimary}, #ffffff);
  font-family: var(${SCOREBOARD_CSS_VARS.fontFamily}, inherit);

  /* Smooth transitions for theme changes */
  transition: background 0.3s ease-in-out, color 0.2s ease-in-out;
`;

const SettingsButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  padding: 8px 16px;
  border: none;
  border-radius: 8px;

  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  color: var(${SCOREBOARD_CSS_VARS.textPrimary}, #ffffff);

  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  /* Icon styling */
  i {
    font-size: 18px;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 24px 40px;

  /* On larger screens, add more padding */
  @media (min-width: 1200px) {
    padding: 32px 64px;
  }
`;

// =============================================================================
// Component
// =============================================================================

/**
 * ScoreboardContainer - Full-page themed container for the scoreboard.
 *
 * Features:
 * - Full viewport height with themed background
 * - Settings button (optional) in top-right
 * - Responsive padding
 * - CSS variables for theming
 *
 * @example
 * ```tsx
 * <ScoreboardThemeProvider initialTheme={settings.theme}>
 *   <ScoreboardContainer showSettingsButton>
 *     <ScoreboardHeader />
 *     <LeaderboardList />
 *   </ScoreboardContainer>
 * </ScoreboardThemeProvider>
 * ```
 */
export const ScoreboardContainer: React.FC<ScoreboardContainerProps> = ({
  children,
  className,
  showSettingsButton = true,
  'data-testid': testId = 'scoreboard-container',
}): React.ReactElement => {
  const { computed, openSettings } = useScoreboardThemeContext();
  const { containerStyle, cssVariables } = computed;

  // Merge CSS variables with container style for inline application
  const combinedStyle: React.CSSProperties = {
    ...containerStyle,
    ...(cssVariables as unknown as React.CSSProperties),
  };

  return (
    <Container
      className={className}
      style={combinedStyle}
      data-testid={testId}
      role='main'
      aria-label='Scoreboard display'
    >
      {showSettingsButton && (
        <SettingsButton
          onClick={openSettings}
          aria-label='Open scoreboard settings'
          data-testid='scoreboard-settings-button'
          type='button'
        >
          <i className='material-icons' aria-hidden='true'>
            settings
          </i>
          <span>Settings</span>
        </SettingsButton>
      )}

      <ContentWrapper>{children}</ContentWrapper>
    </Container>
  );
};

export default ScoreboardContainer;
